import { rd } from '@passionware/monads';
import { Sprout } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { isNonProductionHost } from 'v5/core/environment/nonProductionHost';
import { appRouting } from 'v5/core/routing/appRouting';
import { cn } from 'v5/platform/dom/cn';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';

export interface DataSeederSidebarLinkProps
  extends WithServices<[WithFeatureService, WithAppScopeService]> {
  /** The app's host; the link shows only where the seeder may run. */
  hostname: string;
  className?: string;
}

/** The data seeder's entry at the sidebar foot, behind `data-seeder`. */
export function DataSeederSidebarLink({
  hostname,
  className,
  services
}: DataSeederSidebarLinkProps) {
  const { t } = useTranslation('data-seeder', { keyPrefix: 'page' });
  const isEnabled = rd.getOrElse(
    services.featureService.useFeature(
      services.appScopeService.useOrganizationScopeIdentity(),
      'data-seeder'
    ),
    false
  );
  const organizationId = services.appScopeService.useOrganizationId();

  if (!isEnabled || !isNonProductionHost(hostname)) {
    return null;
  }
  return (
    <Link
      to={appRouting.org(organizationId).organization.dataSeeder()}
      className={cn(
        'flex items-center gap-3 rounded border border-transparent px-3 py-2 text-[13px] text-fg-default transition-colors hocus:border-border-muted hocus:bg-bg-subtle/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
    >
      <Sprout className="size-4 flex-shrink-0" />
      <span className="flex-1 truncate">{t('sidebarLink')}</span>
    </Link>
  );
}
